// SolyticsLabel.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import { FormLabel, IconButton } from '@mui/material';
import { ReactComponent as InfoIcon } from '../assets/InfoIcon.svg';

import SolyticsTooltip from './SolyticsTooltip';

/**
 * SolyticsLabel - A form label component with optional info icon tooltip
 *
 * @component
 * @description
 * SolyticsLabel renders a MUI FormLabel with static positioning, consistent typography,
 * and an optional info icon with tooltip. It inherits error/disabled/focused states from
 * a parent FormControl. Used internally by SolyticsInput, SolyticsDropdownInput, and SolyticsAutocomplete.
 *
 * @param {string} id - The id of the input this label is associated with (used in htmlFor)
 * @param {string} label - Label text to display
 * @param {boolean} [required=false] - If true, displays an asterisk (*) after the label
 * @param {Object} [infoIcon] - Info icon configuration: { tooltip: string, placement?: string, icon?: ReactNode }
 * @param {boolean} [disabled=false] - If true, renders in disabled color state
 * @param {boolean} [error=false] - If true, applies error color state
 * @param {boolean} [focused=false] - If true, applies focused color state
 *
 * @returns {React.ReactElement|null} A FormLabel element, or null if label or id is missing
 */

const SolyticsLabel = ({
  id,
  label,
  required = false,
  infoIcon, // { tooltip, placement, icon }
  disabled = false,
  error = false,
  focused = false,
}) => {
  if (!label || !id) return null;

  return (
    <FormLabel
      htmlFor={id}
      onClick={(e) => e.preventDefault()}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text?.primary,
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '16px',
        letterSpacing: '0.03px',
        marginBottom: '4px',
        pointerEvents: 'auto',
        cursor: 'default',
        '& .MuiFormLabel-asterisk': {
          fontSize: 18,
          lineHeight: 1,
          verticalAlign: 'middle',
          color: theme.palette.error?.main,
        },
        '&.Mui-focused': {
          color:
            theme.palette.lavenderBlue?.[80] || theme.palette.primary?.main,
        },
        '&.Mui-error': {
          color: theme.palette.error?.main,
        },
        '&.Mui-disabled': {
          color: theme.palette.text?.disabled,
        },
      })}
      required={required}
      disabled={disabled}
      error={error}
      focused={focused}
    >
      {label}
      {/* Info Icon With Tooltip */}
      {infoIcon && (
        <SolyticsTooltip
          text={infoIcon?.tooltip}
          placement={infoIcon?.placement || 'top'}
          variant="default"
        >
          <IconButton
            size="small"
            sx={{
              p: 0,
              width: 16,
              height: 16,
              ml: 0.3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {infoIcon?.icon || <InfoIcon style={{ fontSize: 14 }} />}
          </IconButton>
        </SolyticsTooltip>
      )}
    </FormLabel>
  );
};

export default SolyticsLabel;

/**
 * ## Features
 * - Static positioning (no floating/transform behaviour from InputLabel)
 * - Inherits error, disabled, and focused color states from parent FormControl
 * - Optional info icon with SolyticsTooltip (hover to show tooltip)
 * - Pointer events always enabled (tooltip hover works inside FormControl)
 * - Custom or default SVG info icon support
 * - WCAG compliant with htmlFor linking to input id
 *
 * ## Examples
 *
 * ```jsx
 * // Basic label
 * <SolyticsLabel id="email-input" label="Email" />
 *
 * // Required label with info tooltip
 * <SolyticsLabel
 *   id="name-input"
 *   label="Full Name"
 *   required
 *   infoIcon={{ tooltip: 'Enter your legal name', placement: 'right' }}
 * />
 *
 * // Disabled label
 * <SolyticsLabel id="field-input" label="Disabled Field" disabled />
 * ```
 */
